// =====================================================
// Firebase — Auth + Firestore + Storage.
//
// OBS: Firebase-web-config ("apiKey" osv) är PUBLIK per design
// (https://firebase.google.com/docs/projects/api-keys).
// Den identifierar projekt, skyddar inte data. Datan skyddas av:
//   1. Authorized domains (Firebase Console)
//   2. firestore.rules + storage.rules (se repo-roten)
//   3. isAllowed() whitelist nedan
//
// Auth: prefer popup on desktop; fall back to full-page redirect
// when popup fails (mobile browsers, Proton VPN, third-party
// cookie partitioning → often auth/internal-error).
// =====================================================

import { initializeApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as fbSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  type User,
  type AuthError,
} from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

if (!firebaseConfig.apiKey) {
  // Bygget borde ha satt Vite env-variabler. Om det saknas visar vi tydligt fel.
  // eslint-disable-next-line no-console
  console.error(
    'Firebase-konfig saknas. Sätt VITE_FIREBASE_* i .env eller byggmiljön.'
  )
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)
export const storage = getStorage(app)

// Persist login across refreshes
setPersistence(auth, browserLocalPersistence).catch((e) => {
  console.warn('[firebase] setPersistence failed (non-fatal, prefs may reset on reload):', e)
})

// TODO: move email whitelist to Firestore or Firebase custom claims instead of hardcoding
/** Only this Google account is allowed to use the app. */
export const ALLOWED_EMAILS = ['davidrad@gmail.com']

function googleProvider() {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  return provider
}

/** Coarse mobile / in-app browser detection — popup auth is unreliable here. */
function preferRedirectFirst(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua)) return true
  // Narrow viewports often mean phone / tablet even with desktop UA spoof
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) {
    return true
  }
  return false
}

function isPopupFailure(err: unknown): boolean {
  const code = (err as AuthError)?.code || ''
  return (
    code === 'auth/internal-error' ||
    code === 'auth/popup-blocked' ||
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/network-request-failed'
  )
}

/**
 * Complete a redirect-based Google sign-in if the page was returned from Google.
 * Call once on app boot before treating "no user" as logged out.
 */
export async function completeGoogleRedirectIfPresent(): Promise<User | null> {
  try {
    const result = await getRedirectResult(auth)
    return result?.user ?? null
  } catch (e) {
    // Surface to caller via throw — Login / AuthProvider handle messaging
    throw e
  }
}

export async function signInWithGoogle(): Promise<User | null> {
  const provider = googleProvider()

  // Mobile / VPN: skip popup (often auth/internal-error) and go straight to redirect
  if (preferRedirectFirst()) {
    await signInWithRedirect(auth, provider)
    return null // page will navigate away
  }

  try {
    const result = await signInWithPopup(auth, provider)
    return result.user
  } catch (e) {
    if (isPopupFailure(e)) {
      // eslint-disable-next-line no-console
      console.warn('[firebase] popup sign-in failed, falling back to redirect:', (e as AuthError)?.code)
      await signInWithRedirect(auth, provider)
      return null
    }
    throw e
  }
}

export async function signOut() {
  await fbSignOut(auth)
}

export function isAllowed(user: User | null | undefined): boolean {
  if (!user || !user.email) return false
  return ALLOWED_EMAILS.includes(user.email.toLowerCase())
}

export { onAuthStateChanged, type User }
